import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'
import { avatarColor, avatarInitial } from '../lib/avatar'
import { KIND_LABELS, KnowledgeEntry } from './ContextManagementPage'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface ProfileConversation {
  type: 'meeting' | 'email'
  title?: string
  subject?: string
  date: string | null
  documentId?: string
  from?: string
}

interface ProfileActionItem {
  text: string
  documentId: string
  documentTitle: string
  done: boolean
}

interface UpcomingEvent {
  eventId: string
  title: string
  startTime: string | null
  endTime: string | null
  meetingUrl: string | null
}

interface PersonProfile {
  person: { email?: string; name?: string; displayName?: string; photoURL?: string; role?: string; aliases?: string[] }
  conversations: ProfileConversation[]
  upcoming: UpcomingEvent[]
  actionItems: ProfileActionItem[]
  needsReconnect?: boolean
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** タイムライン用の日付ラベル（今日/昨日/M月D日） */
function timelineDayLabel(iso: string | null): string {
  if (!iso) return '日時不明'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '日時不明'
  const diffDays = Math.round((startOfDay(new Date()).getTime() - startOfDay(d).getTime()) / 86400000)
  if (diffDays === 0) return '今日'
  if (diffDays === 1) return '昨日'
  return d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
}

function upcomingLabel(iso: string | null): string {
  if (!iso) return '日時未定'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '日時未定'
  const diffDays = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86400000)
  const day =
    diffDays === 0 ? '今日' : diffDays === 1 ? '明日' : d.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' })
  return `${day} ${formatTime(iso)}`
}

function groupConversationsByDay(items: ProfileConversation[]): { label: string; items: ProfileConversation[] }[] {
  const groups: { label: string; items: ProfileConversation[] }[] = []
  for (const item of items) {
    const label = timelineDayLabel(item.date)
    const last = groups[groups.length - 1]
    if (last && last.label === label) {
      last.items.push(item)
    } else {
      groups.push({ label, items: [item] })
    }
  }
  return groups
}

export function PersonPage() {
  const { id } = useParams<{ id: string }>()
  const { firebaseUser } = useAuth()

  const rawId = (id || '').trim()
  const isEmail = rawId.includes('@')
  const email = isEmail ? rawId.toLowerCase() : ''
  const name = isEmail ? '' : rawId

  const [profile, setProfile] = useState<PersonProfile | null>(null)
  const [showMerge, setShowMerge] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [mergeInput, setMergeInput] = useState('')
  const [merging, setMerging] = useState(false)
  const [knowledge, setKnowledge] = useState<KnowledgeEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const authFetch = useCallback(async (path: string) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
  }, [firebaseUser])

  useEffect(() => {
    if (!firebaseUser || !rawId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const params = new URLSearchParams()
        if (email) params.set('email', email)
        if (name) params.set('name', name)
        const res = await authFetch(`/v1/people/profile?${params.toString()}`)
        const data = await res.json().catch(() => null)
        if (!res.ok) throw new Error(data?.error || `プロフィールの取得に失敗しました (${res.status})`)
        if (!cancelled) setProfile(data as PersonProfile)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'プロフィールの取得に失敗しました')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, rawId])

  // この人物に関する組織ナレッジ（ベストエフォート）
  useEffect(() => {
    if (!firebaseUser || !rawId) return
    let cancelled = false
    ;(async () => {
      try {
        const params = new URLSearchParams({ subjectType: 'person', status: 'active' })
        const res = await authFetch(`/v1/knowledge?${params.toString()}`)
        if (!res.ok) return
        const data = await res.json().catch(() => null)
        if (cancelled || !data) return
        const key = (email || name).toLowerCase()
        const keyBase = key.includes('@') ? key.split('@')[0] : key
        const list: KnowledgeEntry[] = (data.knowledge || []).filter((e: KnowledgeEntry) => {
          const sk = (e.subjectKey || '').toLowerCase()
          return sk === key || sk.includes(keyBase) || keyBase.includes(sk)
        })
        setKnowledge(list)
      } catch {
        // ベストエフォート（セクション非表示のまま）
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser, rawId])

  const handleMerge = async () => {
    const source = mergeInput.trim()
    if (!source || !firebaseUser) return
    setMerging(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/people/merge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, target: email || name })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `統合に失敗しました (${res.status})`)
      setMergeInput('')
      setShowMerge(false)
      // 統合結果を反映するためプロフィールを再取得
      window.location.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : '統合に失敗しました')
    } finally {
      setMerging(false)
    }
  }

  // メールアドレスの手動設定: この表記（名前）をメールアドレスに統合する
  const handleSetEmail = async () => {
    const value = emailInput.trim().toLowerCase()
    if (!value || !firebaseUser || !name) return
    setMerging(true)
    setError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/people/merge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: name, target: value })
      })
      const data = await res.json().catch(() => null)
      if (!res.ok) throw new Error(data?.error || `メールの設定に失敗しました (${res.status})`)
      window.location.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'メールの設定に失敗しました')
    } finally {
      setMerging(false)
    }
  }

  const displayName = name || profile?.person?.name || (email ? email.split('@')[0] : rawId)
  const avatarKey = email || name || rawId
  const groups = groupConversationsByDay(profile?.conversations ?? [])

  return (
    <Layout title={displayName || '人物プロフィール'} showBackButton={true} backPath="/">
      <div className="flex flex-col gap-4" style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
        {/* ── ヘッダー: アバター（プロフィール画像優先）+ 名前 + 連絡先 + 統合 ── */}
        <div className="card">
          <div className="flex items-center gap-4">
            {profile?.person?.photoURL ? (
              <img
                src={profile.person.photoURL}
                alt={displayName}
                className="avatar avatar-xl"
                style={{ objectFit: 'cover', border: 'none' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <span
                className="avatar avatar-xl"
                style={{ backgroundColor: avatarColor(avatarKey), color: '#fff', border: 'none' }}
              >
                {avatarInitial(avatarKey)}
              </span>
            )}
            <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-xl font-semibold truncate">{profile?.person?.displayName || displayName}</h2>
                {profile?.person?.role && (
                  <span className="badge badge-neutral">{profile.person.role === 'admin' ? '管理者' : profile.person.role === 'editor' ? '編集者' : 'メンバー'}</span>
                )}
              </div>
              {/* 連絡先情報 */}
              {(email || profile?.person?.email) ? (
                <a href={`mailto:${email || profile?.person?.email}`} className="text-sm text-secondary truncate" style={{ textDecoration: 'none' }}>
                  ✉ {email || profile?.person?.email}
                </a>
              ) : profile && !loading ? (
                <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                  <input
                    type="email"
                    className="form-input text-sm"
                    style={{ maxWidth: 240, padding: '4px 8px' }}
                    placeholder="メールアドレスを設定"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                  />
                  <button
                    className="btn btn-outline btn-sm"
                    disabled={merging || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.trim())}
                    onClick={handleSetEmail}
                  >
                    {merging && <span className="spinner sm" />}
                    保存
                  </button>
                </div>
              ) : null}
              {profile?.person?.aliases && profile.person.aliases.length > 0 && (
                <span className="text-xs text-tertiary truncate">
                  統合済み: {profile.person.aliases.join('、')}
                </span>
              )}
            </div>
            <button
              className="btn btn-outline btn-sm"
              style={{ flexShrink: 0 }}
              onClick={() => setShowMerge((v) => !v)}
              title="同一人物の別表記をこのプロフィールに統合します"
            >
              人物を統合
            </button>
          </div>
          {showMerge && (
            <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border-light)' }}>
              <input
                type="text"
                className="form-input text-sm"
                style={{ flex: '1 1 220px', padding: '6px 10px' }}
                placeholder="統合する別表記（名前 または メールアドレス）"
                value={mergeInput}
                onChange={(e) => setMergeInput(e.target.value)}
              />
              <button
                className="btn btn-primary btn-sm"
                disabled={merging || !mergeInput.trim()}
                onClick={handleMerge}
              >
                {merging && <span className="spinner sm" />}
                統合する
              </button>
              <p className="text-xs text-tertiary" style={{ width: '100%' }}>
                例: 「話者1」「大江」「takamasa.oe@…」など、同一人物の別表記を入力すると、この人物のタイムラインにまとめて表示されます。
              </p>
            </div>
          )}
        </div>

        {error && <div className="alert danger">{error}</div>}

        {loading ? (
          <div className="flex items-center gap-2 py-8 justify-center">
            <span className="spinner" />
            <span className="text-secondary text-sm">読み込み中...</span>
          </div>
        ) : profile ? (
          <>
            {/* ── アクションアイテム ── */}
            <div className="card">
              <h3 className="text-sm font-semibold mb-3">アクションアイテム</h3>
              {profile.actionItems.length === 0 ? (
                <p className="text-sm text-secondary">この人物に関するアクションアイテムはありません</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {profile.actionItems.map((item, i) => (
                    <div key={i} className="flex items-start gap-2 min-w-0">
                      <input type="checkbox" checked={item.done} readOnly style={{ marginTop: 3, flexShrink: 0 }} />
                      <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                        <span
                          className="text-sm"
                          style={item.done ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                        >
                          {item.text}
                        </span>
                        <Link to={`/documents/${item.documentId}`} className="text-xs text-secondary truncate">
                          {item.documentTitle}
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── ナレッジ ── */}
            {knowledge.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">ナレッジ</h3>
                  <Link
                    to={`/context?subjectType=person&q=${encodeURIComponent(name || email)}`}
                    className="text-xs text-secondary"
                  >
                    コンテキストで見る
                  </Link>
                </div>
                <div className="flex flex-col gap-2">
                  {knowledge.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-2 min-w-0">
                      <span className="badge badge-neutral" style={{ flexShrink: 0 }}>
                        {KIND_LABELS[entry.kind] || entry.kind}
                      </span>
                      <span className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {entry.confidential ? '🤫 ' : ''}{entry.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── 今後の予定 ── */}
            <div className="card">
              <h3 className="text-sm font-semibold mb-3">今後の予定</h3>
              {profile.upcoming.length === 0 ? (
                <p className="text-sm text-secondary">今後14日間に予定されている会議はありません</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {profile.upcoming.map((event) => (
                    <div key={event.eventId} className="flex items-center gap-3 min-w-0">
                      <span style={{ fontSize: 16 }}>📅</span>
                      <div className="flex flex-col min-w-0" style={{ flex: 1 }}>
                        <span className="text-sm font-medium truncate">{event.title || '（無題の予定）'}</span>
                        <span className="text-xs text-secondary">{upcomingLabel(event.startTime)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── タイムライン（ui-kit Activities Log準拠） ── */}
            <div className="card">
              <h3 className="text-sm font-semibold mb-3">タイムライン</h3>
              {profile.needsReconnect && (
                <p className="text-xs text-secondary mb-2">カレンダーを再接続するとメール履歴も表示されます</p>
              )}
              {groups.length === 0 ? (
                <p className="text-sm text-secondary">タイムラインはまだありません</p>
              ) : (
                <div className="flex flex-col">
                  {groups.map((group, gi) => (
                    <div key={group.label} className="flex flex-col">
                      {/* 日付見出し: 「今日 — 2026年7月15日」形式 */}
                      <div
                        className="text-xs font-semibold text-tertiary"
                        style={{ padding: gi === 0 ? '0 0 10px' : '18px 0 10px', letterSpacing: '0.06em' }}
                      >
                        {group.label.toUpperCase()}
                        {group.items[0]?.date && (
                          <span style={{ fontWeight: 500 }}>
                            {' — '}
                            {new Date(group.items[0].date as string).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      {/* 縦レール + 中抜きノード + カード */}
                      <div style={{ position: 'relative', paddingLeft: 24 }}>
                        <span
                          aria-hidden
                          style={{
                            position: 'absolute',
                            left: 5,
                            top: 4,
                            bottom: 0,
                            width: 2,
                            background: 'var(--color-border-light, #e5e7eb)'
                          }}
                        />
                        <div className="flex flex-col" style={{ gap: 12, paddingBottom: 4 }}>
                          {group.items.map((c, i) => (
                            <div key={i} style={{ position: 'relative' }}>
                              {/* 中抜きサークルノード */}
                              <span
                                style={{
                                  position: 'absolute',
                                  left: -24,
                                  top: 16,
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  background: 'var(--color-base, #fff)',
                                  border: '2px solid var(--color-accent, #3b5bdb)'
                                }}
                              />
                              <div
                                className="flex items-start gap-3 min-w-0"
                                style={{
                                  border: '1px solid var(--color-border-light, #e5e7eb)',
                                  borderRadius: 'var(--radius-md, 10px)',
                                  padding: '12px 14px',
                                  background: 'var(--color-base, #fff)'
                                }}
                              >
                                {/* 種別アイコン（丸角スクエア） */}
                                <span
                                  className="flex items-center justify-center"
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    flexShrink: 0,
                                    background: c.type === 'email' ? 'rgba(59,91,219,0.10)' : 'rgba(245,158,11,0.12)',
                                    color: c.type === 'email' ? 'var(--color-accent, #3b5bdb)' : '#d97706'
                                  }}
                                >
                                  {c.type === 'email' ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                                      <rect x="2" y="4" width="20" height="16" rx="2" />
                                      <path d="m22 7-10 6L2 7" />
                                    </svg>
                                  ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                                      <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                                    </svg>
                                  )}
                                </span>
                                <div className="flex flex-col min-w-0" style={{ flex: 1, gap: 2 }}>
                                  {c.type === 'meeting' && c.documentId ? (
                                    <Link to={`/documents/${c.documentId}`} className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
                                      {c.title || '会議'}
                                    </Link>
                                  ) : (
                                    <span className="text-sm font-semibold truncate">
                                      {c.type === 'email' ? c.subject || '（件名なし）' : c.title || '会議'}
                                    </span>
                                  )}
                                  <span className="text-xs text-tertiary truncate">
                                    {formatTime(c.date)}
                                    {c.type === 'email' && c.from ? ` ・ ${c.from}` : ''}
                                    {c.type === 'meeting' ? ' ・ 会議' : ' ・ メール'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  )
}
